/**
 * WeatherSummaryTab
 *
 * This component provides a comprehensive tabular view of weather conditions across all towers
 * in the power grid network, enabling quick comparison and analysis.
 *
 * Key Features:
 * - Sortable data table with all towers
 * - Search and filtering capabilities
 * - Risk level indicators
 * - Pagination for large datasets
 *
 * Data Display:
 * - Tower identification
 * - Section/location information
 * - Current weather parameters:
 *   - Wind speed
 *   - Temperature
 *   - Humidity
 *   - Precipitation
 * - Overall risk assessment
 *
 * Risk Classification:
 * - Green: All parameters within safe limits
 * - Yellow: One or more parameters at cautionary levels
 * - Red: Critical thresholds exceeded
 *
 * Use Cases:
 * - Network-wide weather monitoring
 * - Comparative analysis
 * - Risk pattern identification
 * - Maintenance prioritization
 */

"use client"

import React from "react"
import { useState, useEffect, useCallback } from "react" // Added useCallback
import { View, StyleSheet, ScrollView, ActivityIndicator } from "react-native"
import { Text, useTheme, DataTable, Button, Searchbar } from "react-native-paper"
import Constants from "expo-constants"

interface WeatherSummaryTabProps {
  towers: any[]
  onTowerSelect: (tower: any) => void
  selectedTower: any
}

export const WeatherSummaryTab: React.FC<WeatherSummaryTabProps> = ({ towers, onTowerSelect, selectedTower }) => {
  const theme = useTheme()
  const [page, setPage] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [searchQuery, setSearchQuery] = useState("")
  const [towerWeatherData, setTowerWeatherData] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSimulatedData, setIsSimulatedData] = useState(false)

  // Memoize fetchTowerWeather to prevent recreation on each render
  const fetchTowerWeather = useCallback(async (tower: any) => {
    try {
      console.debug(`[WeatherSummaryTab] Fetching weather data for tower ${tower.name}`)
      const API_KEY = Constants.expoConfig?.extra?.TOMORROW_IO_API_KEY

      const response = await fetch(
        `https://api.tomorrow.io/v4/weather/realtime?location=${tower.lat},${tower.lng}&apikey=${API_KEY}`,
      )

      if (!response.ok) {
        throw new Error(`API call failed: ${response.statusText}`)
      }

      const data = await response.json()
      return {
        windSpeed: data.data.values.windSpeed,
        temperature: data.data.values.temperature,
        humidity: data.data.values.humidity,
        precipitation: data.data.values.precipitationIntensity,
        lightning: data.data.values.lightningStrikeCount || 0,
      }
    } catch (error) {
      console.warn(`[WeatherSummaryTab] Failed to fetch data for tower ${tower.name}, using simulation`)
      return {
        windSpeed: Math.round(5 + Math.random() * 30),
        temperature: Math.round(15 + Math.random() * 15),
        humidity: Math.round(40 + Math.random() * 50),
        precipitation: Math.round(Math.random() * 10) / 10,
        lightning: Math.random() > 0.9 ? Math.round(Math.random() * 3) : 0,
      }
    }
  }, []) // Empty dependency array since it doesn't depend on any props or state

  // Memoize fetchAllTowerData to prevent recreation on each render
  const fetchAllTowerData = useCallback(async () => {
    if (!towers || towers.length === 0) return

    setIsLoading(true)
    setIsSimulatedData(false)

    try {
      console.debug("[WeatherSummaryTab] Starting batch weather data fetch for all towers")
      const towerData = await Promise.all(
        towers.map(async (tower) => {
          const weather = await fetchTowerWeather(tower)

          // Determine risk level
          let riskLevel = "Low"
          let riskColor = "#52c41a" // green

          if (weather.windSpeed > 50 || weather.lightning > 0) {
            riskLevel = "High"
            riskColor = "#f5222d" // red
          } else if (weather.windSpeed > 30 || weather.precipitation > 2) {
            riskLevel = "Medium"
            riskColor = "#faad14" // yellow
          }

          return {
            ...tower,
            weather: {
              ...weather,
              riskLevel,
              riskColor,
            },
          }
        }),
      )

      setTowerWeatherData(towerData)
      setIsSimulatedData(false)
    } catch (error) {
      console.error("[WeatherSummaryTab] Error fetching tower data:", error)
      setIsSimulatedData(true)
    } finally {
      setIsLoading(false)
    }
  }, [towers, fetchTowerWeather])

  // Use effect with proper dependencies
  useEffect(() => {
    fetchAllTowerData()

    // Set up refresh interval (every 5 minutes)
    const intervalId = setInterval(fetchAllTowerData, 5 * 60 * 1000)

    // Cleanup interval on unmount
    return () => clearInterval(intervalId)
  }, [fetchAllTowerData]) // Only depend on fetchAllTowerData

  // Memoize filtered towers to prevent unnecessary recalculations
  const filteredTowers = React.useMemo(
    () =>
      towerWeatherData.filter(
        (tower) =>
          tower.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (tower.parent?.name && tower.parent.name.toLowerCase().includes(searchQuery.toLowerCase())),
      ),
    [towerWeatherData, searchQuery],
  )

  const from = page * itemsPerPage
  const to = Math.min((page + 1) * itemsPerPage, filteredTowers.length)
  const paginatedTowers = filteredTowers.slice(from, to)

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <View>
          <Text variant="headlineMedium">Tower Weather Summary</Text>
          {isSimulatedData && (
            <Text
              variant="bodyMedium"
              style={{
                color: theme.colors.error,
                marginTop: 4,
              }}
            >
              ⚠️ Some towers are showing simulated data due to API limitations
            </Text>
          )}
        </View>
        <Searchbar
          placeholder="Search towers"
          onChangeText={setSearchQuery}
          value={searchQuery}
          style={styles.searchBar}
        />
      </View>

      <View style={styles.legendContainer}>
        <View style={styles.legendItem}>
          <View style={[styles.legendColor, { backgroundColor: "#52c41a" }]} />
          <Text>Low Risk</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendColor, { backgroundColor: "#faad14" }]} />
          <Text>Medium Risk</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendColor, { backgroundColor: "#f5222d" }]} />
          <Text>High Risk</Text>
        </View>
      </View>

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={{ marginTop: 16 }}>Loading tower weather data...</Text>
        </View>
      ) : (
        <ScrollView style={styles.tableContainer}>
          <DataTable>
            <DataTable.Header>
              <DataTable.Title>Tower ID</DataTable.Title>
              <DataTable.Title>Section</DataTable.Title>
              <DataTable.Title numeric>Wind (km/h)</DataTable.Title>
              <DataTable.Title numeric>Temp (°C)</DataTable.Title>
              <DataTable.Title numeric>Humidity (%)</DataTable.Title>
              <DataTable.Title numeric>Precip (mm)</DataTable.Title>
              <DataTable.Title>Risk Level</DataTable.Title>
              <DataTable.Title>Actions</DataTable.Title>
            </DataTable.Header>

            {paginatedTowers.map((tower) => (
              <DataTable.Row
                key={tower.id}
                style={
                  selectedTower && selectedTower.id === tower.id ? { backgroundColor: "rgba(24, 144, 255, 0.1)" } : {}
                }
              >
                <DataTable.Cell>{tower.name}</DataTable.Cell>
                <DataTable.Cell>{tower.parent?.name || "-"}</DataTable.Cell>
                <DataTable.Cell numeric>{tower.weather.windSpeed}</DataTable.Cell>
                <DataTable.Cell numeric>{tower.weather.temperature}</DataTable.Cell>
                <DataTable.Cell numeric>{tower.weather.humidity}</DataTable.Cell>
                <DataTable.Cell numeric>{tower.weather.precipitation}</DataTable.Cell>
                <DataTable.Cell>
                  <View style={[styles.riskBadge, { backgroundColor: tower.weather.riskColor }]}>
                    <Text style={styles.riskText}>{tower.weather.riskLevel}</Text>
                  </View>
                </DataTable.Cell>
                <DataTable.Cell>
                  <Button mode="contained" compact onPress={() => onTowerSelect(tower)} style={{ marginRight: 4 }}>
                    View
                  </Button>
                </DataTable.Cell>
              </DataTable.Row>
            ))}

            <DataTable.Pagination
              page={page}
              numberOfPages={Math.ceil(filteredTowers.length / itemsPerPage)}
              onPageChange={(page) => setPage(page)}
              label={`${from + 1}-${to} of ${filteredTowers.length}`}
              showFastPaginationControls
              numberOfItemsPerPageList={[5, 10, 20]}
              numberOfItemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
              selectPageDropdownLabel={"Rows per page"}
            />
          </DataTable>
        </ScrollView>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    marginBottom: 16,
  },
  searchBar: {
    marginTop: 8,
    marginBottom: 16,
  },
  legendContainer: {
    flexDirection: "row",
    marginBottom: 16,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 16,
  },
  legendColor: {
    width: 16,
    height: 16,
    borderRadius: 8,
    marginRight: 5,
  },
  tableContainer: {
    flex: 1,
  },
  riskBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    alignSelf: "flex-start",
  },
  riskText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 12,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
})

